import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { signInPathFor } from '../lib/returnTo';

interface Props {
  children: ReactNode;
  /** Optional platform-role gate. When set, the route is only reachable for
   *  users whose ID-token claim `platformRoles` includes at least one of
   *  these roles. Lacking the role redirects to /shell rather than /signin
   *  (the user is authenticated, just unprivileged for this route). */
  requiredPlatformRoles?: string[];
}

export function ProtectedRoute({ children, requiredPlatformRoles }: Props) {
  const { loading, session } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background text-sm text-muted-foreground">
        Loading…
      </div>
    );
  }
  if (!session) {
    // Carry the page (path + query + hash) to sign-in so the visitor lands
    // back on it afterwards — OAuth callbacks need their `code` / `state`.
    return <Navigate to={signInPathFor(location, window.location.origin)} replace />;
  }
  if (
    requiredPlatformRoles &&
    !requiredPlatformRoles.some((r) => session.platformRoles.includes(r))
  ) {
    return <Navigate to="/shell" replace />;
  }
  return <>{children}</>;
}
